import React, { useEffect, useState } from 'react';
import { Sensor, SensorChannel } from '../../types';
import {
  useGetChannelConfigQuery,
  useUpsertChannelConfigMutation,
  useDeleteChannelConfigMutation,
} from '../../services/sensorApi';

interface Props {
  sensor: Sensor;
  onClose: () => void;
}

interface ChannelForm {
  display_name: string;
  high_alarm: string;
  low_alarm: string;
  high_enable: boolean;
  low_enable: boolean;
  alarms_disabled: boolean;
}

const emptyForm = (name: string): ChannelForm => ({
  display_name: name,
  high_alarm: '',
  low_alarm: '',
  high_enable: false,
  low_enable: false,
  alarms_disabled: false,
});

export const SplitChannelDialog: React.FC<Props> = ({ sensor, onClose }) => {
  const { data: existing, isLoading } = useGetChannelConfigQuery(sensor.id);
  const [upsert, { isLoading: isSaving }] = useUpsertChannelConfigMutation();
  const [deleteSplit, { isLoading: isDeleting }] = useDeleteChannelConfigMutation();

  const [ch1, setCh1] = useState<ChannelForm>(emptyForm(`${sensor.name} (Ch 1)`));
  const [ch2, setCh2] = useState<ChannelForm>(emptyForm(`${sensor.name} (Ch 2)`));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    const c1 = existing.find(c => c.channel === 1);
    const c2 = existing.find(c => c.channel === 2);
    if (c1) {
      setCh1({
        display_name: c1.display_name,
        high_alarm: c1.high_alarm != null ? String(c1.high_alarm) : '',
        low_alarm: c1.low_alarm != null ? String(c1.low_alarm) : '',
        high_enable: c1.high_enable,
        low_enable: c1.low_enable,
        alarms_disabled: c1.alarms_disabled,
      });
    }
    if (c2) {
      setCh2({
        display_name: c2.display_name,
        high_alarm: c2.high_alarm != null ? String(c2.high_alarm) : '',
        low_alarm: c2.low_alarm != null ? String(c2.low_alarm) : '',
        high_enable: c2.high_enable,
        low_enable: c2.low_enable,
        alarms_disabled: c2.alarms_disabled,
      });
    }
  }, [existing]);

  const isSplit = existing && existing.length === 2;

  const handleSave = async () => {
    setError(null);
    try {
      await upsert({
        sensorId: sensor.id,
        channels: [
          {
            channel: 1,
            display_name: ch1.display_name || `${sensor.name} (Ch 1)`,
            high_alarm: ch1.high_alarm !== '' ? parseFloat(ch1.high_alarm) : null,
            low_alarm: ch1.low_alarm !== '' ? parseFloat(ch1.low_alarm) : null,
            high_enable: ch1.high_enable,
            low_enable: ch1.low_enable,
            alarms_disabled: ch1.alarms_disabled,
          },
          {
            channel: 2,
            display_name: ch2.display_name || `${sensor.name} (Ch 2)`,
            high_alarm: ch2.high_alarm !== '' ? parseFloat(ch2.high_alarm) : null,
            low_alarm: ch2.low_alarm !== '' ? parseFloat(ch2.low_alarm) : null,
            high_enable: ch2.high_enable,
            low_enable: ch2.low_enable,
            alarms_disabled: ch2.alarms_disabled,
          },
        ],
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to save channel config');
    }
  };

  const handleRemoveSplit = async () => {
    setError(null);
    try {
      await deleteSplit(sensor.id).unwrap();
      onClose();
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to remove split');
    }
  };

  const ChannelForm: React.FC<{
    label: string;
    form: ChannelForm;
    onChange: (f: ChannelForm) => void;
  }> = ({ label, form, onChange }) => (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="font-medium text-sm text-gray-700">{label}</div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Display Name</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.display_name}
          onChange={e => onChange({ ...form, display_name: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">High Alarm (°C)</label>
          <input
            type="number"
            step="0.1"
            className="w-full border rounded px-2 py-1 text-sm"
            value={form.high_alarm}
            onChange={e => onChange({ ...form, high_alarm: e.target.value })}
          />
          <label className="flex items-center gap-1 mt-1 text-xs">
            <input
              type="checkbox"
              checked={form.high_enable}
              onChange={e => onChange({ ...form, high_enable: e.target.checked })}
            />
            Enable
          </label>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Low Alarm (°C)</label>
          <input
            type="number"
            step="0.1"
            className="w-full border rounded px-2 py-1 text-sm"
            value={form.low_alarm}
            onChange={e => onChange({ ...form, low_alarm: e.target.value })}
          />
          <label className="flex items-center gap-1 mt-1 text-xs">
            <input
              type="checkbox"
              checked={form.low_enable}
              onChange={e => onChange({ ...form, low_enable: e.target.checked })}
            />
            Enable
          </label>
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-600">
        <input
          type="checkbox"
          checked={form.alarms_disabled}
          onChange={e => onChange({ ...form, alarms_disabled: e.target.checked })}
        />
        Disable all alarms for this channel
      </label>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Configure Channel Split — {sensor.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {isLoading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ChannelForm label="Channel 1" form={ch1} onChange={setCh1} />
            <ChannelForm label="Channel 2" form={ch2} onChange={setCh2} />
          </div>
        )}

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="text-xs text-gray-500">
          Delay, repeat, and battery alarm settings are shared from the parent sensor alarm config.
        </div>

        <div className="flex items-center justify-between pt-2">
          <div>
            {isSplit && (
              <button
                onClick={handleRemoveSplit}
                disabled={isDeleting}
                className="text-sm text-red-600 hover:underline disabled:opacity-50"
              >
                {isDeleting ? 'Removing...' : 'Remove Split'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
